import { Card, Flex, Modal, Typography } from "antd"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"

import { nextPageUrl } from "../../../../../../../redux/shared/paged-list.ts"
import type { RootState } from "../../../../../../../store.ts"
import { extractParamsFromNextUrl } from "../../../../../../../utils/form/extract-params-from-next-url.ts"
import { serializeData } from "../../../../../../../utils/utils"
import { type SubcontractorFormData } from "../../../../../../SubcontractorsPage/components/SubcontractorForm/subcontractor-form-data.ts"
import {
  createChecklistItem,
  deleteChecklistItem,
  fetchChecklistItems,
  fetchChecklistItemsFilterOptions,
  patchChecklistItem,
  reorderChecklistItems
} from "../../../../../redux/project-checklist/project-checklist.thunks.ts"
import { projectSetProp } from "../../../../../redux/project.slice.ts"
import { createAndAssignSubcontractor } from "../../../../../redux/project.thunks.ts"
import type { ProjectChecklistFilters } from "../../../../../redux/project.types.ts"
import type { ProjectChecklistItem } from "../../../../../types/project-checklists.types.tsx"
import { CHECKLIST_ITEM_STATUSES } from "../../../../constants/checklist-item-constants.ts"
import { selectProjectWritePermissions } from "../../../../../helpers/project-permissions.selectors.ts"
import { ProjectViewChecklistsList } from "./components/ProjectViewChecklistsList.tsx"
import { ProjectViewChecklistsListFilters } from "./components/ProjectViewChecklistsListFilters.tsx"

interface ProjectViewChecklistsListSectionProps extends ConnectedProps<
  typeof connector
> {
  projectId: string
}

function ProjectViewChecklistsListSection({
  userId,
  projectId,
  data,
  fetching,
  filters,
  permissions,
  projectPermissions,
  getChecklistItems,
  getFilterOptions,
  createItem,
  updateItem,
  removeItem,
  reorderItems,
  addSubcontractor,
  setProp
}: ProjectViewChecklistsListSectionProps) {
  const { t } = useTranslation()
  const checklistItems = data?.results || []
  const count = data?.count || 0

  const [editableRow, setEditableRow] = useState<string>("")
  const [isAdding, setIsAdding] = useState(false)

  const setFilters = useCallback(
    (data: Partial<ProjectChecklistFilters>) => {
      setProp({
        key: "checklistItemsFilters",
        value: { ...filters, ...data }
      })
    },
    [setProp, filters]
  )

  const onAddTask = useCallback(() => {
    setIsAdding(true)
  }, [])

  const handleGetFilterOptions = useCallback(async () => {
    return getFilterOptions(projectId).unwrap()
  }, [getFilterOptions, projectId])

  const handleFilterChange = useCallback(
    async (data: Partial<ProjectChecklistFilters>) => {
      setFilters({ ...data, page: 1 })
    },
    [setFilters]
  )

  const handleChangePage = useCallback(
    (page: number) => {
      setFilters({ page })
    },
    [setFilters]
  )

  const handleGetChecklistItems = useCallback(async () => {
    const serializeFilters = serializeData(
      filters || {}
    ) as ProjectChecklistFilters

    await getChecklistItems({
      isMore: false,
      params: {
        project: projectId,
        ...serializeFilters
      }
    })
  }, [getChecklistItems, projectId, filters])

  const nextUrl = nextPageUrl(data)
  const handleGetMoreChecklistItems = useCallback(async () => {
    if (!nextUrl) return

    const serializeFilters =
      extractParamsFromNextUrl<ProjectChecklistFilters>(nextUrl)

    await getChecklistItems({
      isMore: true,
      params: { project: projectId, ...serializeFilters }
    })
  }, [nextUrl, getChecklistItems, projectId])

  const handleCreateItem = async (data: ProjectChecklistItem) => {
    try {
      await createItem({
        ...data,
        project: projectId
      }).unwrap()
      setIsAdding(false)
      await handleGetChecklistItems()
    } catch (e) {
      console.log("Create checklist item error: ", e)
    }
  }

  const handleUpdateItem = async (data: ProjectChecklistItem) => {
    try {
      const isDone = data?.status === CHECKLIST_ITEM_STATUSES.DONE

      await updateItem({
        ...data,
        is_done: isDone,
        id: editableRow || data?.id
      })
      await handleGetChecklistItems()
      setEditableRow("")
    } catch (e) {
      console.log("Update checklist item error: ", e)
    }
  }

  const handleDeleteItem = async (itemId: string) => {
    Modal.confirm({
      title: t("projectTasks.deleteConfirm.title"),
      content: t("projectTasks.deleteConfirm.content"),
      okText: t("projectTasks.deleteConfirm.ok"),
      okType: "danger",
      cancelText: t("projectTasks.deleteConfirm.cancel"),
      onOk: async () => {
        try {
          await removeItem(itemId).unwrap()
          await handleGetChecklistItems()
        } catch (e) {
          console.log("Delete checklist item error: ", e)
        }
      }
    })
  }

  const handleReorderTasks = useCallback(
    (orderedIds: string[]) => {
      void reorderItems(orderedIds.map((id, order) => ({ id, order })))
    },
    [reorderItems]
  )

  const handleNewAddSubcontractor = useCallback(
    async ({ send_invitation, ...data }: SubcontractorFormData) => {
      try {
        return await addSubcontractor({
          projectId,
          data: { ...data, invite: send_invitation }
        }).unwrap()
      } catch (e) {
        console.log("Add project subcontractor error:", e)
        return null
      }
    },
    [addSubcontractor, projectId]
  )

  const onSelectRow = (record: ProjectChecklistItem, action: string) => {
    switch (action) {
      case "edit_row":
        return setEditableRow(record?.id)
      case "update":
        return handleUpdateItem(record)
      case "cancel":
        return setEditableRow("")
      case "create":
        return handleCreateItem(record)
      case "cancel_create":
        return setIsAdding(false)
      case "delete_row":
        return handleDeleteItem(record?.id)
    }
  }

  useEffect(() => {
    void handleGetChecklistItems()
  }, [handleGetChecklistItems])

  useEffect(() => {
    void handleGetFilterOptions()
  }, [handleGetFilterOptions])

  return (
    <Card
      title={
        <Typography.Title level={4} className="m-0">
          Active Tasks
        </Typography.Title>
      }
    >
      <Flex vertical gap={"middle"}>
        <ProjectViewChecklistsListFilters
          showFilterByAssignee={
            !!projectPermissions?.checklists
              ?.project_checklist_show_filter_by_assignee
          }
          filters={filters}
          getFilterOptions={handleGetFilterOptions}
          onFilterChange={handleFilterChange}
        />
        <ProjectViewChecklistsList
          userId={userId}
          projectPermissions={projectPermissions}
          fetching={fetching}
          checklistItems={checklistItems}
          next={data?.next}
          retryNext={data?.retryNext}
          count={count}
          onLoadMore={handleGetMoreChecklistItems}
          editableRow={editableRow}
          isAdding={isAdding}
          addSubcontractorAllowed={
            permissions?.subcontractors?.subcontractors?.subcontractor_create
          }
          createAllowed={
            projectPermissions?.checklists?.project_checklist_create
          }
          onAddTask={onAddTask}
          onReorderTasks={handleReorderTasks}
          onSelectRow={onSelectRow}
          onPageChange={handleChangePage}
          addNewAddSubcontractor={handleNewAddSubcontractor}
        />
      </Flex>
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  userId: state.auth.userInfo.id,
  fetching: state.project.fetchingSection,
  data: state.project.checklistItems,
  filters: state.project.checklistItemsFilters,
  permissions: state.auth.permissions?.codes,
  projectPermissions: selectProjectWritePermissions(state)
})
const mapDispatchToProps = {
  getChecklistItems: fetchChecklistItems,
  getFilterOptions: fetchChecklistItemsFilterOptions,
  createItem: createChecklistItem,
  updateItem: patchChecklistItem,
  removeItem: deleteChecklistItem,
  reorderItems: reorderChecklistItems,
  addSubcontractor: createAndAssignSubcontractor,
  setProp: projectSetProp
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectViewChecklistsListSection)
